export type PortCallDateEntry = {
  id?: string | null;
  arrivalDate?: unknown;
  departureDate?: unknown;
};

type ParsedPortCallDateEntry = {
  arrivalDate: Date;
  departureDate: Date;
};

const asNormalizedDate = (value: unknown): Date | null => {
  const parsed = value instanceof Date ? value : typeof value === "string" ? new Date(value) : null;

  if (!parsed || Number.isNaN(parsed.getTime())) {
    return null;
  }

  const normalized = new Date(parsed);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

export const getConflictingPortCallIndexes = (entries: PortCallDateEntry[]): Set<number> => {
  const conflictingIndexes = new Set<number>();

  const parsedEntries = entries.map<ParsedPortCallDateEntry | null>((entry) => {
    const arrivalDate = asNormalizedDate(entry.arrivalDate);
    const departureDate = asNormalizedDate(entry.departureDate);

    if (!arrivalDate || !departureDate || departureDate.getTime() < arrivalDate.getTime()) {
      return null;
    }

    return { arrivalDate, departureDate };
  });

  parsedEntries.forEach((leftEntry, leftIndex) => {
    if (!leftEntry) {
      return;
    }

    for (let rightIndex = leftIndex + 1; rightIndex < parsedEntries.length; rightIndex += 1) {
      const rightEntry = parsedEntries[rightIndex];

      if (!rightEntry) {
        continue;
      }

      const leftArrival = leftEntry.arrivalDate.getTime();
      const leftDeparture = leftEntry.departureDate.getTime();
      const rightArrival = rightEntry.arrivalDate.getTime();
      const rightDeparture = rightEntry.departureDate.getTime();

      const hasDuplicateSameDayEntry =
        leftArrival === leftDeparture &&
        rightArrival === rightDeparture &&
        leftArrival === rightArrival;
      const hasInteriorOverlap =
        Math.max(leftArrival, rightArrival) < Math.min(leftDeparture, rightDeparture);

      if (hasDuplicateSameDayEntry || hasInteriorOverlap) {
        conflictingIndexes.add(leftIndex);
        conflictingIndexes.add(rightIndex);
      }
    }
  });

  return conflictingIndexes;
};

export const hasAnyPortCallDateConflicts = (entries: PortCallDateEntry[]): boolean =>
  getConflictingPortCallIndexes(entries).size > 0;

export const hasPortCallDateConflict = ({
  candidate,
  existingEntries,
  currentPortCallId,
}: {
  candidate: PortCallDateEntry;
  existingEntries?: PortCallDateEntry[];
  currentPortCallId?: string;
}): boolean => {
  const comparableEntries = (existingEntries ?? []).filter(
    (entry) => entry.id !== currentPortCallId,
  );
  const candidateIndex = comparableEntries.length;
  const conflicts = getConflictingPortCallIndexes([...comparableEntries, candidate]);

  return conflicts.has(candidateIndex);
};
